import { useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getAssetsForProjectItem } from '../api/projects';

const assetCache = new Map<string, any[]>();
const assetLoadingPromises = new Map<string, Promise<any[]>>();

export const useProjectItemsAssetsLazy = (projectId: string, itemId: string) => {
  const { keycloak: { token } } = useKeycloak();
  const [assets, setAssets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAssets = async () => {
    if (!token || !projectId || !itemId) return;

    const cacheKey = `${projectId}:${itemId}`;

    // If already cached, use immediately
    if (assetCache.has(cacheKey)) {
      setAssets(assetCache.get(cacheKey) || []);
      return;
    }

    // If currently loading, wait for that promise
    if (assetLoadingPromises.has(cacheKey)) {
      setLoading(true);
      assetLoadingPromises.get(cacheKey)!.then(data => {
        setAssets(data || []);
        setLoading(false);
      }).catch(() => {
        setAssets([]);
        setLoading(false);
      });
      return;
    }

    setLoading(true);
    const promise = getAssetsForProjectItem(token, projectId, itemId)
      .then(data => {
        const result = Array.isArray(data) ? data : [];
        assetCache.set(cacheKey, result);
        assetLoadingPromises.delete(cacheKey);
        return result;
      })
      .catch(error => {
        console.error('Failed to load assets:', error);
        assetLoadingPromises.delete(cacheKey);
        return [];
      });

    assetLoadingPromises.set(cacheKey, promise);

    promise.then(data => {
      setAssets(data);
      setLoading(false);
    }).catch(() => {
      setAssets([]);
      setLoading(false);
    });
  };

  return { assets, loading, fetchAssets };
};
